import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import { api, type TemplateSummary } from '../../lib/api';
import { BUILTIN_TEMPLATES } from '../../lib/companyTemplates';
import { ConfirmDialog } from '../ConfirmDialog';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Company templates: built-in starters (read-only) and ones admins published from decks. */
export function TemplatesTab() {
  const [templates, setTemplates] = useState<TemplateSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<TemplateSummary | null>(null);

  const load = useCallback(
    () =>
      api
        .listTemplates()
        .then((r) => setTemplates(r.templates))
        .catch((err) => {
          setTemplates([]);
          setError(err instanceof Error ? err.message : 'Could not load templates');
        }),
    [],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const row = (key: string, title: string, detail: string, action?: React.ReactNode, first = false) => (
    <Stack key={key} direction="row" spacing={1.5} sx={{ alignItems: 'center', px: 2, py: 1.25, borderTop: first ? 0 : 1, borderColor: 'divider' }}>
      <DashboardCustomizeOutlinedIcon sx={{ color: 'primary.main' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {title}
        </Typography>
        <Typography variant="caption" noWrap component="div">
          {detail}
        </Typography>
      </Box>
      {action}
    </Stack>
  );

  return (
    <Stack spacing={2.5}>
      <Typography variant="body2" color="text.secondary">
        Templates appear when someone starts a new deck. To add one, open a deck and use Deck → Publish as company
        template.
      </Typography>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box>
        <Typography variant="overline" component="h3" sx={{ color: 'text.secondary' }}>
          Published by admins
        </Typography>
        {templates === null ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
            <CircularProgress size={22} aria-label="Loading templates" />
          </Box>
        ) : templates.length ? (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            {templates.map((t, i) =>
              row(
                t.id,
                t.title,
                `${t.description || 'No description'} · ${t.slide_count ?? '?'} slides · ${t.author_name}, ${formatDate(t.created_at)}`,
                <Tooltip title="Delete template">
                  <IconButton size="small" aria-label={`Delete ${t.title}`} onClick={() => setToDelete(t)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>,
                i === 0,
              ),
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            None yet.
          </Typography>
        )}
      </Box>

      <Box>
        <Typography variant="overline" component="h3" sx={{ color: 'text.secondary' }}>
          Built in
        </Typography>
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          {BUILTIN_TEMPLATES.map((t, i) =>
            row(
              t.id,
              t.title,
              `${t.description} · ${t.slides.length} slides`,
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                Always available
              </Typography>,
              i === 0,
            ),
          )}
        </Box>
      </Box>

      <ConfirmDialog
        open={Boolean(toDelete)}
        title="Delete template?"
        message={`“${toDelete?.title ?? ''}” won't be offered for new decks any more. Decks already made from it aren't affected.`}
        confirmLabel="Delete template"
        destructive
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          const t = toDelete;
          if (t)
            void api
              .deleteTemplate(t.id)
              .then(load)
              .catch((err) => setError(err instanceof Error ? err.message : 'Could not delete the template'));
        }}
      />
    </Stack>
  );
}
